import Modal from "@/components/Modal/Modal";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { combineClasses } from "@/lib/utils";
import { formatDate } from "@/lib/dateFormat";
import type { FlaggedComment } from "@/services/api/commentReports";
import styles from "./ReportDetailsModal.module.scss";
import { useEffect, useState } from "react";

const PAGE_SIZE = 5;
export interface ReportDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  // null while closed/animating out — the modal unmounts its content via
  // `Modal`'s own `isOpen` check, so this only needs to be non-null while
  // actually visible.
  comment: FlaggedComment | null;
}

// `reports[].createdAt` is a real datetime, not a bare date (see
// FlaggedCommentReport in services/api/commentReports.ts) — show both,
// same date/time split AuditLog's auditFormat.ts uses for the same reason.
const formatReportDate = (iso: string) => formatDate(iso);

const formatReportTime = (iso: string) => formatDate(iso, { hour: "2-digit", minute: "2-digit", hour12: false });

// Page-specific rather than a shared component — the only place a
// `FlaggedComment`'s nested `reports` list needs its own view. Opened by
// clicking the "N reports" count in PastEvents.tsx's flagged comments card.
const ReportDetailsModal = ({ isOpen, onClose, comment }: ReportDetailsModalProps) => {
  const [visibleReports, setVisibleReports] = useState(PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(()=>{
    if(!isOpen) return;
    setVisibleReports(PAGE_SIZE)
    setIsLoadingMore(false)
  },[isOpen,comment])

  const hasMore = visibleReports < (comment?.reports.length ?? 0);

  useEffect(() => {
    if (!isLoadingMore) return;
    const timeout = setTimeout(() => {
      setVisibleReports((previous) =>
        Math.min(previous + PAGE_SIZE, comment?.reports.length ?? 0),
      );
      setIsLoadingMore(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [isLoadingMore, comment?.reports.length]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (isLoadingMore || !hasMore) return;

    const element = event.currentTarget;

    const isNearBottom =
      element.scrollHeight - element.scrollTop <=
      element.clientHeight + 50;

    if (!isNearBottom) return;

    setIsLoadingMore(true);
  };
  return(
  <Modal isOpen={isOpen} onClose={onClose} className={combineClasses(styles.modal)}>
    <div className={styles.header}>
      <div>
        <h2 className={styles.title}>Report Details</h2>
        {comment && (
          <p className={styles.subtitle}>
            {comment.reportCount} report{comment.reportCount === 1 ? "" : "s"} on{" "}
            <strong>{comment.commentAuthorName}</strong>&apos;s comment
          </p>
        )}
      </div>
      <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
        <CrossIcon withBg={false} height={18} width={18} stroke="currentColor" strokeWidth={2.5} />
      </button>
    </div>

    {comment && (
      <>
        <p className={styles.quote}>&quot;{comment.commentText}&quot;</p>

        <div className={styles.reportList} onScroll={handleScroll}>
          {comment?.reports?.slice(0,visibleReports).map((report) => (
            <div key={report.reportId} className={styles.reportRow}>
              <div className={styles.reportRowHeader}>
                <span className={styles.reporterName}>{report.reportedByName}</span>
                <span className={styles.reasonBadge}>{report.reasonName}</span>
              </div>
              <span className={styles.reporterEmail}>{report.reportedByEmail}</span>
              <span className={styles.reportedAt}>
                {formatReportDate(report.createdAt)} · {formatReportTime(report.createdAt)}
              </span>
            </div>
          ))}
          {isLoadingMore && (
            <div className={styles.loadingMore}>
              <span className={styles.spinner} aria-label="Loading more reports" />
            </div>
          )}
        </div>
      </>
    )}
  </Modal>
  )
};

export default ReportDetailsModal;
